import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { produce } from 'immer';
import { merge } from 'lodash';

export const ERROR_TIMEOUT = 60000;
export const DEFAULT_DISENGAGE_TIMEOUT = 3000;
export const DEFAULT_DEBOUNCE_TIMEOUT = 500;

export interface DispatchOptions {
  debounceMs?: number;
  lockTimeout?: number; // Override for disengage timeout (replaces DEFAULT_DISENGAGE_TIMEOUT)
}

export interface OptimisticStateParams<T, A extends { type: string }> {
  actualState: T;
  reducer: (state: T, action: A) => T;
  mergeToStore?: (result: Partial<T>, action: A) => void;
}

export interface OptimisticStateReturn<T, A extends { type: string }> {
  state: T;
  isOptimistic: boolean;
  dispatch: (action: A, command?: () => void, options?: DispatchOptions) => void;
  dispatchAsync: <R = Partial<T>>(action: A, request: Promise<R>) => Promise<R>;
  engageLock: () => void;
  disengageLock: (timeout?: number) => void;
  error: string | null;
}

type InternalAction<T, A> = A | { type: '__APPLY_ACTUAL_STATE__'; payload: T };

export const useOptimisticState = <T, A extends { type: string }>({
  actualState,
  reducer,
  mergeToStore,
}: OptimisticStateParams<T, A>): OptimisticStateReturn<T, A> => {
  // Internal reducer that handles __APPLY_ACTUAL_STATE__ before consumer reducer
  const internalReducer = useCallback(
    (state: T, action: InternalAction<T, A>) => {
      // Check if it's our internal action
      if (
        typeof action === 'object' &&
        action !== null &&
        'type' in action &&
        action.type === '__APPLY_ACTUAL_STATE__'
      ) {
        return (action as { type: '__APPLY_ACTUAL_STATE__'; payload: T }).payload;
      }
      return reducer(state, action as A);
    },
    [reducer],
  );

  const [state, baseDispatch] = useReducer(internalReducer, actualState);
  const [isOptimistic, setIsOptimistic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorTimeout = useRef<ReturnType<typeof setTimeout>>();

  const lock = useRef<{
    value: boolean;
    newOptimisticState: T;
    timer?: ReturnType<typeof setTimeout>;
  }>({
    value: true, // Start engaged to block socket reconnection replays
    newOptimisticState: actualState,
    timer: undefined,
  });

  const debouncedCommands = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const lockEngageTime = useRef<number>(0);

  // Internal setState for applying actual state
  const setState = useCallback((newState: T) => {
    baseDispatch({ type: '__APPLY_ACTUAL_STATE__', payload: newState });
    setIsOptimistic(false);
  }, []);

  const engageLock = useCallback(() => {
    clearTimeout(lock.current.timer);
    lock.current.value = true;
    lockEngageTime.current = Date.now();

    clearTimeout(errorTimeout.current);
    setError(null);
    errorTimeout.current = setTimeout(() => {
      setError('Operation timed out after 1 minute. Please check your connection and try again.');
    }, ERROR_TIMEOUT);
  }, []);

  const disengageLock = useCallback(
    (timeout = DEFAULT_DISENGAGE_TIMEOUT) => {
      if (lock.current.timer) {
        clearTimeout(lock.current.timer);
      }

      lock.current.timer = setTimeout(() => {
        lock.current.value = false;
        setState(lock.current.newOptimisticState);
      }, timeout);
    },
    [setState],
  );

  const dispatch = useCallback(
    (action: A, command?: () => void, options?: DispatchOptions) => {
      const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_TIMEOUT;
      const lockTimeout = options?.lockTimeout ?? DEFAULT_DISENGAGE_TIMEOUT;

      engageLock(); // Step 1: Prevent interference
      baseDispatch(action); // Step 2: Apply optimistic change immediately
      setIsOptimistic(true);

      if (command) {
        // Step 3: Debounce the command execution per action type
        const actionKey = JSON.stringify(action.type);
        const existingTimer = debouncedCommands.current.get(actionKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        const newTimer = setTimeout(() => {
          try {
            command();
          } catch {
            // Silently handle command errors
          }
          debouncedCommands.current.delete(actionKey);
        }, debounceMs);

        debouncedCommands.current.set(actionKey, newTimer);
      }

      // Step 4: Extend lock timeout - debounce time + custom lock timeout
      const extendedTimeout = debounceMs + lockTimeout;
      disengageLock(extendedTimeout);
    },
    [engageLock, disengageLock, baseDispatch],
  );

  // Promise-based dispatch - lock tied to promise lifecycle with store integration
  const dispatchAsync = useCallback(
    async <R = Partial<T>>(action: A, request: Promise<R>): Promise<R> => {
      engageLock(); // Step 1: Prevent interference
      baseDispatch(action); // Step 2: Apply optimistic change immediately
      setIsOptimistic(true);

      try {
        const result = await request; // Step 3: Wait for server response

        // Step 4: Merge result into global store using action-specific logic
        if (mergeToStore) {
          mergeToStore(result as Partial<T>, action);
        }

        setIsOptimistic(false);
        disengageLock(0); // Step 5: Unlock immediately - store update will trigger actualState change

        return result;
      } catch (e) {
        // On error: revert optimistic change
        setState(actualState);
        setIsOptimistic(false);
        setError(e instanceof Error ? e.message : 'Operation failed');
        disengageLock(0);
        throw e;
      }
    },
    [engageLock, disengageLock, setState, actualState, mergeToStore],
  );

  useEffect(() => {
    clearTimeout(errorTimeout.current);
    setError(null);

    // Merge new actual state with current optimistic state
    const newOptimisticState = produce(state, (draft) => {
      // Deep merge actual state into current optimistic state
      merge(draft, actualState);
    });

    lock.current.newOptimisticState = newOptimisticState;

    if (!lock.current.value) {
      setState(newOptimisticState);
    }
  }, [actualState]);

  useEffect(() => {
    disengageLock(DEFAULT_DISENGAGE_TIMEOUT);

    return () => {
      clearTimeout(errorTimeout.current);
      clearTimeout(lock.current.timer);
    };
  }, [disengageLock]);

  return {
    state,
    isOptimistic,
    dispatch,
    dispatchAsync,
    engageLock,
    disengageLock,
    error,
  };
};
